import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SearchSuggestions from './SearchSuggestions';
import SearchProductGrid from './SearchProductGrid';

// Sugerencias rotativas para el placeholder
const ROTATING_SUGGESTIONS = [
  'Vitamina C',
  'Retinol',
  'Ácido Hialurónico',
  'Niacinamida',
  'Protector Solar',
  'Sérum antiedad',
  'Hidratante',
  'Limpiador facial'
];

// Sugerencias en pills para mobile (se rotan cada 5s)
const MOBILE_SUGGESTION_SETS = [
  ['Limpiadores', 'Sueros', 'Hidratantes', 'Exfoliantes', 'Vitamina C'],
  ['Retinol', 'Niacinamida', 'Ceramidas', 'Péptidos', 'Tónicos'],
  ['Acné', 'Manchas', 'Luminosidad', 'Ácido Hialurónico', 'Tratamientos'],
];

export default function SearchModal({ isOpen, onClose, products = [] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');
  const [mobileSuggestionSet, setMobileSuggestionSet] = useState(0);
  const savedScrollY = useRef(0);
  const [portalContainer, setPortalContainer] = useState(null);
  const inputRef = useRef(null);

  // Create portal container on mount
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalContainer(document.body);
    }
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
  }, [isOpen]);

  // Efecto de typing para el placeholder rotativo
  useEffect(() => {
    if (!isOpen || searchTerm) return;

    const currentSuggestion = ROTATING_SUGGESTIONS[currentPlaceholder];
    let charIndex = 0;
    let isDeleting = false;
    let timeout;

    const type = () => {
      if (!isDeleting) {
        if (charIndex <= currentSuggestion.length) {
          setDisplayedPlaceholder(currentSuggestion.slice(0, charIndex));
          charIndex++;
          timeout = setTimeout(type, 80);
        } else {
          timeout = setTimeout(() => {
            isDeleting = true;
            type();
          }, 2000);
        }
      } else {
        if (charIndex > 0) {
          charIndex--;
          setDisplayedPlaceholder(currentSuggestion.slice(0, charIndex));
          timeout = setTimeout(type, 40);
        } else {
          isDeleting = false;
          setCurrentPlaceholder((prev) => (prev + 1) % ROTATING_SUGGESTIONS.length);
        }
      }
    };

    type();

    return () => clearTimeout(timeout);
  }, [isOpen, searchTerm, currentPlaceholder]);

  // Rotate mobile suggestion pills every 5s
  useEffect(() => {
    if (!isOpen || searchTerm) return;
    const interval = setInterval(() => {
      setMobileSuggestionSet(prev => (prev + 1) % MOBILE_SUGGESTION_SETS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen, searchTerm]);

  // Generate dynamic suggestions based on search query
  useEffect(() => {
    if (!searchTerm.trim()) {
      setDynamicSuggestions([]);
      return;
    }

    const query = searchTerm.toLowerCase();
    const allSuggestions = new Set();

    products.forEach((product) => {
      if (
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query)
      ) {
        if (product.category) allSuggestions.add(product.category);
        if (product.badge) allSuggestions.add(product.badge);
      }
    });

    setDynamicSuggestions(Array.from(allSuggestions).slice(0, 10));
  }, [searchTerm, products]);

  // Filter products with loading simulation
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setFilteredProducts([]);
      setSelectedSuggestion(null);
      setDynamicSuggestions([]);
      setCurrentPlaceholder(0);
      setDisplayedPlaceholder('');
      return;
    }

    if (!searchTerm.trim() && !selectedSuggestion) {
      setFilteredProducts([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    const timer = setTimeout(() => {
      const query = searchTerm.toLowerCase();
      let results = [...products];

      if (query) {
        results = results.filter((product) =>
          product.name?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query) ||
          (product.badge && product.badge.toLowerCase().includes(query)) ||
          (product.tags && product.tags.some(tag => tag.toLowerCase().includes(query))) ||
          (product.categories && product.categories.some(cat => cat.toLowerCase().includes(query)))
        );
      }

      if (selectedSuggestion) {
        results = results.filter((product) =>
          product.category === selectedSuggestion ||
          product.badge === selectedSuggestion
        );
      }

      setFilteredProducts(results);
      setIsSearching(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedSuggestion, isOpen, products]);

  // Scroll lock and ESC key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      savedScrollY.current = window.scrollY;
      document.addEventListener('keydown', handleKeyDown);
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      if (savedScrollY.current > 0 && !isOpen) {
        window.scrollTo(0, savedScrollY.current);
      }
    };
  }, [isOpen, onClose]);

  const handleSuggestionClick = (suggestion) => {
    setSelectedSuggestion(suggestion);
  };

  // Don't render if not open or no portal container
  if (!isOpen || !portalContainer) return null;

  // Determinar si mostrar el estado inicial (sin búsqueda)
  const showInitialState = !searchTerm.trim() && !selectedSuggestion;

  // Modal content
  const modalContent = (
    <div 
      className="phys-search-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        backgroundColor: 'var(--phys-snow)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'searchFadeIn 0.3s ease forwards'
      }}
    >
      {/* Header with Search Input */}
      <div 
        className="border-b border-[var(--phys-frost)] bg-[var(--phys-snow)] sticky top-0 z-10"
        style={{ flexShrink: 0 }}
      >
        <div className="max-w-[2550px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Search Icon */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--phys-fog)"
              strokeWidth="1.5"
              className="flex-shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>

            {/* Search Input with rotating placeholder */}
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedSuggestion(null);
                }}
                placeholder=""
                className="w-full text-base font-light text-[var(--phys-ink)] bg-transparent border-none outline-none focus:outline-none focus:ring-0"
                style={{ boxShadow: 'none' }}
              />
              {/* Custom placeholder with animation */}
              {!searchTerm && (
                <div className="absolute inset-0 flex items-center pointer-events-none">
                  <span className="text-base font-light text-[var(--phys-fog)]">
                    Buscar "{displayedPlaceholder}"
                    <span className="animate-pulse">|</span>
                  </span>
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 hover:bg-[var(--phys-mist)] rounded-full transition-colors"
              aria-label="Cerrar búsqueda"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="var(--phys-fog)"
                strokeWidth="1.5"
              >
                <path d="M1 1L15 15M15 1L1 15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Suggestion Pills - sticky below search bar */}
      {showInitialState && (
        <div
          className="md:hidden border-b border-[var(--phys-frost)] bg-[var(--phys-snow)]"
          style={{ flexShrink: 0 }}
        >
          <div className="px-4 py-4">
            <p className="text-xs uppercase tracking-wider text-[#999] font-light mb-3">
              Sugerencias
            </p>
            <div className="flex flex-wrap gap-2" style={{ transition: 'opacity 0.3s ease' }}>
              {MOBILE_SUGGESTION_SETS[mobileSuggestionSet].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setSearchTerm(suggestion)}
                  className="px-4 py-2 text-sm font-light text-[var(--phys-ink)] border border-[var(--phys-frost)] rounded-full hover:bg-[#333] hover:text-white transition-all"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Body with Sidebar + Products */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full max-w-[2550px] mx-auto flex">
          {/* Sidebar - Suggestions */}
          <SearchSuggestions
            suggestions={dynamicSuggestions}
            selectedSuggestion={selectedSuggestion}
            onSuggestionClick={handleSuggestionClick}
            searchTerm={searchTerm}
          />

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto">
            <SearchProductGrid
              products={filteredProducts}
              isSearching={isSearching}
              searchTerm={searchTerm}
              selectedSuggestion={selectedSuggestion}
              showInitialState={showInitialState}
              onProductClick={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, portalContainer);
}
